// src/components/orders/ManualEsimModal.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'react-toastify';
import Modal from '../common/Modal';
import apiClient from '../../services/ApiClient';
import styles from './ManualEsimModal.module.css';

// Bölge ve kıta listeleri, ilgili yönetim sayfalarındaki sabitlerle aynı.
const REGIONS = [
    { id: 1, name: 'Avrupa' },
    { id: 2, name: 'Asya' },
    { id: 3, name: 'Kuzey Amerika' },
    { id: 4, name: 'Latin Amerika' },
    { id: 5, name: 'Afrika' },
    { id: 7, name: 'Pasifik' },
    { id: 6, name: 'Orta Doğu' },
];

const CONTINENTS = [
    { id: 1, name: 'Avrupa' },
    { id: 2, name: 'Asya' },
    { id: 3, name: 'Kuzey Amerika' },
    { id: 4, name: 'Latin Amerika' },
    { id: 5, name: 'Afrika' },
    { id: 7, name: 'Pasifik' },
    { id: 6, name: 'Orta Doğu' },
    { id: 0, name: 'Global' },
];

const CATEGORIES = [
    { key: 'country', label: 'Ülke Paketi' },
    { key: 'region', label: 'Bölge Paketi' },
    { key: 'prepaid', label: 'Ön Ödemeli Paket' },
];

// Idempotency anahtarı üretimi. crypto.randomUUID modern tarayıcılarda vardır;
// güvenli olmayan (http) ortamlar için basit bir yedek bırakıyoruz.
const genRequestId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

const ManualEsimModal = ({ isOpen, onClose, onCreated }) => {
    // requestId form açılışında bir kez üretilir; tekrar gönderimler (retry) güvenlidir.
    const [requestId, setRequestId] = useState(() => genRequestId());

    // Paket kategorisi
    const [category, setCategory] = useState('country');

    // Ülke kaynağı (bir kez çekilir), bölge/kıta kaynağı (seçime göre çekilir)
    const [countriesData, setCountriesData] = useState([]);
    const [scopeProducts, setScopeProducts] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [dataError, setDataError] = useState(null);

    // Form seçimleri
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedRegionId, setSelectedRegionId] = useState('');
    const [selectedContinentId, setSelectedContinentId] = useState('');
    const [selectedRefillId, setSelectedRefillId] = useState('');
    const [email, setEmail] = useState('');

    // Gönderim / sonuç durumları
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);       // success: true yanıtı
    const [resultError, setResultError] = useState(null); // { error, orderId }
    const [copiedKey, setCopiedKey] = useState(null);

    const qrRef = useRef(null);

    // --- Veri çekme ---
    const fetchCountries = useCallback(async () => {
        setIsLoadingData(true);
        setDataError(null);
        try {
            const response = await apiClient.get('/admin/countries-data');
            if (response.data && Array.isArray(response.data.data)) {
                setCountriesData(response.data.data);
            } else {
                throw new Error('Ülke paket verileri beklenen formatta gelmedi.');
            }
        } catch (err) {
            console.error('Manuel eSIM ülke listesi yükleme hatası:', err);
            setDataError('Ülke paket listesi yüklenemedi. Bağlantıyı kontrol edip tekrar deneyin.');
            setCountriesData([]);
        } finally {
            setIsLoadingData(false);
        }
    }, []);

    const fetchScope = useCallback(async (url) => {
        setIsLoadingData(true);
        setDataError(null);
        setScopeProducts([]);
        try {
            const response = await apiClient.get(url);
            if (response.data && Array.isArray(response.data.data)) {
                setScopeProducts(response.data.data[0]?.products || []);
            } else {
                throw new Error('Paket verileri beklenen formatta gelmedi.');
            }
        } catch (err) {
            console.error(`Manuel eSIM paket listesi yükleme hatası (${url}):`, err);
            setDataError('Paket listesi yüklenemedi. Bağlantıyı kontrol edip tekrar deneyin.');
            setScopeProducts([]);
        } finally {
            setIsLoadingData(false);
        }
    }, []);

    // Ülke listesi yalnızca gerektiğinde ve bir kez çekilir.
    useEffect(() => {
        if (isOpen && category === 'country' && countriesData.length === 0) {
            fetchCountries();
        }
    }, [isOpen, category, countriesData.length, fetchCountries]);

    // Bölge / kıta seçimine göre paketleri çek.
    useEffect(() => {
        if (!isOpen) return;
        if (category === 'region' && selectedRegionId) {
            fetchScope(`/regions-data/${selectedRegionId}`);
        } else if (category === 'prepaid' && selectedContinentId !== '') {
            fetchScope(`/lifetime-data/${selectedContinentId}`);
        }
    }, [isOpen, category, selectedRegionId, selectedContinentId, fetchScope]);

    // Ülke listesi (alfabetik, benzersiz)
    const countryOptions = useMemo(() => {
        return countriesData
            .map((item) => ({ name: item.country, flag: item.flag }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [countriesData]);

    // Seçili kaynağın paketleri (product > refill düzleştirilmiş).
    // Ülke refill'lerinde kimlik alanı `refillId`, bölge/ön ödemeli refill'lerinde `id`.
    const refillOptions = useMemo(() => {
        const opts = [];
        if (category === 'country') {
            const countryItem = countriesData.find((c) => c.country === selectedCountry);
            (countryItem?.products || []).forEach((product) => {
                product.refills.forEach((refill) => {
                    opts.push({ id: String(refill.refillId), label: `${product.name} — ${refill.title}` });
                });
            });
        } else {
            scopeProducts.forEach((product) => {
                product.refills.forEach((refill) => {
                    opts.push({ id: String(refill.id), label: `${product.name} — ${refill.title}` });
                });
            });
        }
        return opts;
    }, [category, countriesData, selectedCountry, scopeProducts]);

    const handleCategoryChange = (e) => {
        setCategory(e.target.value);
        setSelectedCountry('');
        setSelectedRegionId('');
        setSelectedContinentId('');
        setSelectedRefillId('');
        setScopeProducts([]);
        setDataError(null);
    };

    const handleScopeChange = (setter) => (e) => {
        setter(e.target.value);
        setSelectedRefillId(''); // kaynak değişince paket seçimini sıfırla
    };

    const resetForm = () => {
        setResult(null);
        setResultError(null);
        setCategory('country');
        setSelectedCountry('');
        setSelectedRegionId('');
        setSelectedContinentId('');
        setSelectedRefillId('');
        setScopeProducts([]);
        setEmail('');
        setRequestId(genRequestId()); // yeni hat = yeni requestId
    };

    const handleClose = () => {
        onClose();
    };

    const handleSubmit = async () => {
        if (!selectedRefillId) {
            toast.error('Lütfen bir paket seçin.');
            return;
        }
        setSubmitting(true);
        setResultError(null);

        const body = {
            productId: selectedRefillId,
            requestId,
        };
        const trimmedEmail = email.trim();
        if (trimmedEmail) body.userEmail = trimmedEmail;

        try {
            const response = await apiClient.post('/api/v1/admin/esims/manual', body);
            const data = response.data || {};
            // Panel HTTP koduna değil `success` bayrağına bakar.
            if (data.success) {
                setResult(data);
                toast.success('eSIM başarıyla oluşturuldu.');
                if (onCreated) onCreated();
            } else {
                setResultError({
                    error: data.error || 'eSIM oluşturulamadı.',
                    orderId: data.orderId,
                });
            }
        } catch (err) {
            // 400 / 404 gibi durumlarda gövde error alanını taşır
            const data = err.response?.data;
            console.error('Manuel eSIM oluşturma hatası:', err);
            setResultError({
                error: data?.error || 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.',
                orderId: data?.orderId,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopy = (key, value) => {
        if (!value) return;
        navigator.clipboard.writeText(value).then(
            () => {
                setCopiedKey(key);
                setTimeout(() => setCopiedKey(null), 1500);
            },
            () => toast.error('Kopyalanamadı. Lütfen manuel olarak kopyalayın.')
        );
    };

    const handleDownloadPng = () => {
        const canvas = qrRef.current?.querySelector('canvas');
        if (!canvas) {
            toast.error('QR görseli hazır değil.');
            return;
        }
        const url = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        const iccid = result?.data?.iccid || result?.orderId || 'esim';
        a.href = url;
        a.download = `esim-qr-${iccid}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    // --- Ekran içeriği seçimi ---
    let title = 'Manuel eSIM Oluştur';
    if (result) title = 'eSIM Oluşturuldu';
    else if (resultError) title = 'eSIM Oluşturulamadı';

    // Paket seçici için "önce şunu seç" mesajı
    const scopeChosen =
        (category === 'country' && selectedCountry) ||
        (category === 'region' && selectedRegionId) ||
        (category === 'prepaid' && selectedContinentId !== '');

    const renderScopeSelect = () => {
        if (category === 'country') {
            return (
                <div className={styles.field}>
                    <label htmlFor="me-country">Ülke</label>
                    <select
                        id="me-country"
                        value={selectedCountry}
                        onChange={handleScopeChange(setSelectedCountry)}
                        disabled={submitting || isLoadingData}
                        className={styles.select}
                    >
                        <option value="">Ülke seçin…</option>
                        {countryOptions.map((c) => (
                            <option key={c.name} value={c.name}>{c.name}</option>
                        ))}
                    </select>
                </div>
            );
        }
        if (category === 'region') {
            return (
                <div className={styles.field}>
                    <label htmlFor="me-region">Bölge</label>
                    <select
                        id="me-region"
                        value={selectedRegionId}
                        onChange={handleScopeChange(setSelectedRegionId)}
                        disabled={submitting}
                        className={styles.select}
                    >
                        <option value="">Bölge seçin…</option>
                        {REGIONS.map((r) => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                    </select>
                </div>
            );
        }
        // prepaid
        return (
            <div className={styles.field}>
                <label htmlFor="me-continent">Kıta</label>
                <select
                    id="me-continent"
                    value={selectedContinentId}
                    onChange={handleScopeChange(setSelectedContinentId)}
                    disabled={submitting}
                    className={styles.select}
                >
                    <option value="">Kıta seçin…</option>
                    {CONTINENTS.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
            </div>
        );
    };

    const renderForm = () => {
        const selectedRefill = refillOptions.find((r) => r.id === selectedRefillId);

        let packagePlaceholder = 'Paket seçin…';
        if (!scopeChosen) packagePlaceholder = 'Önce yukarıdan seçim yapın';
        else if (isLoadingData) packagePlaceholder = 'Yükleniyor…';
        else if (refillOptions.length === 0) packagePlaceholder = 'Bu kaynak için paket bulunamadı';

        return (
            <div className={styles.form}>
                <div className={styles.field}>
                    <label htmlFor="me-category">Paket Türü</label>
                    <select
                        id="me-category"
                        value={category}
                        onChange={handleCategoryChange}
                        disabled={submitting}
                        className={styles.select}
                    >
                        {CATEGORIES.map((c) => (
                            <option key={c.key} value={c.key}>{c.label}</option>
                        ))}
                    </select>
                </div>

                {renderScopeSelect()}

                <div className={styles.field}>
                    <label htmlFor="me-package">Paket</label>
                    <select
                        id="me-package"
                        value={selectedRefillId}
                        onChange={(e) => setSelectedRefillId(e.target.value)}
                        disabled={submitting || !scopeChosen || isLoadingData || refillOptions.length === 0}
                        className={styles.select}
                    >
                        <option value="">{packagePlaceholder}</option>
                        {refillOptions.map((r) => (
                            <option key={r.id} value={r.id}>{r.label}</option>
                        ))}
                    </select>
                    {selectedRefill && (
                        <span className={`${styles.hint} num`}>Ürün ID: {selectedRefill.id}</span>
                    )}
                    {dataError && <span className={styles.hintError}>{dataError}</span>}
                </div>

                <div className={styles.field}>
                    <label htmlFor="me-email">Müşteri e-postası (opsiyonel)</label>
                    <input
                        id="me-email"
                        type="email"
                        placeholder="musteri@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={submitting}
                        className={styles.input}
                    />
                    <span className={styles.hint}>
                        Girilirse eSIM müşterinin uygulama hesabına bağlanır ve kalan GB'ını uygulamadan görebilir.
                    </span>
                </div>

                <div className={styles.warning}>
                    Bu işlem <strong>ödeme almadan</strong> KeepGo'da gerçek bir hat açar ve maliyet oluşturur.
                </div>

                <div className={styles.actions}>
                    <button
                        type="button"
                        className={styles.secondaryButton}
                        onClick={handleClose}
                        disabled={submitting}
                    >
                        Vazgeç
                    </button>
                    <button
                        type="button"
                        className={styles.primaryButton}
                        onClick={handleSubmit}
                        disabled={submitting || !selectedRefillId}
                    >
                        {submitting ? 'Oluşturuluyor…' : 'Oluştur'}
                    </button>
                </div>
            </div>
        );
    };

    const renderSuccess = () => {
        const plan = result.data?.purchasedPlan;
        const iccid = result.data?.iccid;
        const orderId = result.orderId || result.data?.orderId;
        const lpa = result.esimActivationCode || result.data?.qrCodeData;
        const manualCode = result.data?.manualActivationCode;

        return (
            <div className={styles.result}>
                <div className={styles.qrWrap} ref={qrRef}>
                    {lpa ? (
                        <QRCodeCanvas value={lpa} size={220} level="M" includeMargin />
                    ) : (
                        <p className={styles.hint}>QR verisi bulunamadı.</p>
                    )}
                </div>

                {plan && (
                    <div className={styles.planCard}>
                        <div className={styles.planTitle}>{plan.countryName}</div>
                        <div className={styles.planMeta}>
                            <span>{plan.dataAmount}</span>
                            <span>·</span>
                            <span>{plan.validity}</span>
                            {plan.price && (<><span>·</span><span className="num">{plan.price}</span></>)}
                        </div>
                    </div>
                )}

                <dl className={styles.detailList}>
                    {iccid && (
                        <div className={styles.detailRow}>
                            <dt>ICCID</dt>
                            <dd className="num">{iccid}</dd>
                        </div>
                    )}
                    {orderId && (
                        <div className={styles.detailRow}>
                            <dt>Sipariş No</dt>
                            <dd className="num">{orderId}</dd>
                        </div>
                    )}
                </dl>

                <div className={styles.copyButtons}>
                    {lpa && (
                        <button
                            type="button"
                            className={styles.secondaryButton}
                            onClick={() => handleCopy('lpa', lpa)}
                        >
                            {copiedKey === 'lpa' ? 'Kopyalandı!' : 'LPA kodunu kopyala'}
                        </button>
                    )}
                    {manualCode && (
                        <button
                            type="button"
                            className={styles.secondaryButton}
                            onClick={() => handleCopy('manual', manualCode)}
                        >
                            {copiedKey === 'manual' ? 'Kopyalandı!' : 'Manuel aktivasyon kodunu kopyala'}
                        </button>
                    )}
                    {lpa && (
                        <button type="button" className={styles.secondaryButton} onClick={handleDownloadPng}>
                            QR'ı PNG indir
                        </button>
                    )}
                </div>

                <div className={styles.actions}>
                    <button type="button" className={styles.secondaryButton} onClick={resetForm}>
                        Yeni eSIM oluştur
                    </button>
                    <button type="button" className={styles.primaryButton} onClick={handleClose}>
                        Kapat
                    </button>
                </div>
            </div>
        );
    };

    const renderError = () => (
        <div className={styles.result}>
            <div className={styles.errorPanel}>
                <span className={styles.errorIcon} aria-hidden="true">⚠</span>
                <p className={styles.errorText}>{resultError.error}</p>
                {resultError.orderId && (
                    <p className={styles.hint}>
                        Sipariş No: <span className="num">{resultError.orderId}</span> — Siparişler ekranında görülebilir.
                    </p>
                )}
            </div>
            <div className={styles.actions}>
                <button type="button" className={styles.secondaryButton} onClick={resetForm}>
                    Tekrar dene
                </button>
                <button type="button" className={styles.primaryButton} onClick={handleClose}>
                    Kapat
                </button>
            </div>
        </div>
    );

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title={title}>
            {result ? renderSuccess() : resultError ? renderError() : renderForm()}
        </Modal>
    );
};

export default ManualEsimModal;
